// Overgezet uit naslag/server/imports/api/calculations/calculations.ts (de pure berekeningen;
// de methodes `updateCalculations` en `updateCookies` staan in logica/calculations.ts).
import { floor } from '../hulp.js';
import { chanceForAMalfunctionOnNextRoll, impactTotalMalfunctionBuildUp, } from './MalfunctionCalculations.js';
import { getAssetData } from '../data/assetData.js';
import { calculateShippingTime, downTimePerAsset, downTimePerAssetMinimum, } from './CalculationPerAsset.js';
import { availabilityFactory, availabilityPercentagePerAsset, availabilityPercentageVm, impactPerAsset, impactVormers, } from './BufferTimeCalculation.js';
import { TaskType } from '../types/tasks.js';
import { InGameEffect } from '../types/inGameEffect.js';
import { cookieProduction } from './CookieImpactCalculations.js';
import { Asset } from '../types/assets.js';
import { DieOption } from '../types/randomizers.js';
import { isAangekomen, ligtOpen } from '../data/magazijn.js';
export const getYellowSidesFromDice = (dice) => {
    const temp = {};
    Object.entries(dice).forEach((singleAsset) => {
        const asset = singleAsset[0];
        const data = singleAsset[1];
        temp[asset] = data.die.filter((dieSide) => dieSide === DieOption.yellow).length;
    });
    return temp;
};
export const getMechanicsKnowledge = (mechanics) => {
    let totalKnowledge = 0;
    mechanics.forEach((mechanic) => {
        totalKnowledge += mechanic.knowledgeLevel;
    });
    return totalKnowledge;
};
export const calculateReliability = (yellowSides, asset, currentMalfunctionBuildUp) => {
    const reliability = floor(1 - impactTotalMalfunctionBuildUp(yellowSides, asset, currentMalfunctionBuildUp), 1);
    return reliability < 0 ? 0 : reliability;
};
export const countMaintenanceTasks = (tasks, asset) => {
    const tempTasks = tasks.filter((task) => 
    // TODO: Al deze taken tellen als onderhoud? (vraag uit het origineel)
    [
        TaskType.onderhoud,
        TaskType.veiligheid,
        TaskType.revisie,
        TaskType.storing,
        TaskType.incident,
    ].includes(task.type) && task.specificAsset === asset);
    return tempTasks.length;
};
export const getPartsNecesarry = (tasks) => {
    const spareParts = [];
    tasks.forEach((task) => {
        if (task.needsSparepart && task.sparepartNeeded)
            spareParts.push(task.sparepartNeeded);
    });
    return spareParts;
};
// Niet in het origineel: een doos die nog onderweg is, telt niet (`ligtOpen`).
export const getAccessibleSpareparts = (parts) => {
    const spareParts = [];
    parts.forEach((part) => {
        if (ligtOpen(part))
            spareParts.push(part.part);
    });
    return spareParts;
};
/**
 * Niet in het origineel: het magazijn zoals het deze week telt. Wat de taken van deze week
 * eruit haalden (`onderdelenGebruikt`), telt nog mee; anders gaf een onderdeel dat er was
 * toch levertijd. Wat deze week gekocht is, is nog onderweg en telt pas volgende week.
 */
export const magazijnVanDeWeek = (game) => [
    ...game.storageContent.filter((p) => !!p && isAangekomen(p)),
    ...(game.onderdelenGebruikt ?? []).map((part) => ({ part, open: true })),
];
/**
 * Niet in het origineel: de taken in de volgorde van `volgorde` (de id's in de volgorde van
 * kiezen). `getSpecificTaskData` geeft ze in de volgorde van de kaarten; een taak die niet in
 * `volgorde` staat, komt achteraan.
 */
export const inVolgorde = (taken, volgorde) => {
    const plek = (taak) => {
        const index = volgorde.indexOf(taak._id);
        return index === -1 ? volgorde.length : index;
    };
    return [...taken].sort((a, b) => plek(a) - plek(b));
};
/**
 * Niet in het origineel: de taken die hun onderdeel met spoed moeten bestellen. De open
 * onderdelen in het magazijn van deze week gaan naar de taken in de volgorde van `taken`
 * (de volgorde van kiezen); een taak die geen open onderdeel meer krijgt, moet bestellen.
 *
 * De enige plek waar die verdeling gebeurt: de spoedtoeslag bij het kiezen
 * (`spoedbestellingen` en `spoedVoor`) en de levertijd in de berekening (`getCalculations`)
 * gebruiken allebei deze functie. Zo staat de machine stil van precies de taak waarvoor het
 * team spoed betaalde.
 */
export const takenMetSpoed = (game, taken) => {
    const open = getAccessibleSpareparts(magazijnVanDeWeek(game));
    const spoed = [];
    for (const taak of taken) {
        if (!taak.needsSparepart || !taak.sparepartNeeded)
            continue;
        const index = open.indexOf(taak.sparepartNeeded);
        if (index === -1)
            spoed.push(taak);
        else
            open.splice(index, 1);
    }
    return spoed;
};
export const getCalculations = (game, assets, tasks) => {
    const [impactBonusPresent, sparePartBonusPresent] = [
        game.dataManagementSystemData >= 4,
        game.dataManagementSystemData >= 3,
    ];
    const complexityPerAsset = {};
    const assetData = getAssetData();
    const originalAssetData = getAssetData();
    Object.entries(originalAssetData).forEach((singleAsset) => {
        const asset = singleAsset[0];
        const data = singleAsset[1];
        complexityPerAsset[asset] = data.complexity;
    });
    const yellowSidesPerAsset = getYellowSidesFromDice(game.dashboard.dice);
    const assetIncidentMalfunctionData = assets ?? game.assets;
    // Niet in het origineel: wat de taken van deze week uit het magazijn haalden, telt voor deze
    // week nog mee (`onderdelenGebruikt`). Anders gaf een onderdeel dat er was toch levertijd.
    const magazijn = magazijnVanDeWeek(game);
    const mechanicsKnowledge = getMechanicsKnowledge(game.mechanics);
    // Complexe berekeningen
    const reliabilityPerAsset = {};
    Object.entries(assetIncidentMalfunctionData).forEach((singleAsset) => {
        const asset = singleAsset[0];
        reliabilityPerAsset[asset] = calculateReliability(yellowSidesPerAsset[asset], asset, assetIncidentMalfunctionData[asset].malfunctionBuildUp);
    });
    // In het origineel berekend maar nergens gebruikt; hier ook, om dicht bij de bron te blijven.
    const malfunction1xroll = {};
    Object.entries(assetIncidentMalfunctionData).forEach((singleAsset) => {
        const asset = singleAsset[0];
        malfunction1xroll[asset] = chanceForAMalfunctionOnNextRoll(yellowSidesPerAsset[asset], asset, assetIncidentMalfunctionData[asset].malfunctionBuildUp);
    });
    // Niet zoals het origineel: daar kreeg elke machine een eigen kopie van het magazijn (met de
    // vraag "TODO: Right now shippingTime holds for all assets ... Is that intended?"). Nu één
    // gedeeld magazijn, verdeeld in de volgorde van kiezen zoals de spoedtoeslag
    // (`takenMetSpoed`): de levertijd hoort bij de machine van de taak waarvoor spoed betaald is.
    const spoed = takenMetSpoed(game, inVolgorde(tasks, game.tasksOnTable));
    const shippingtimePerAsset = Object.fromEntries(Object.values(Asset).map((asset) => [
        asset,
        calculateShippingTime(getPartsNecesarry(spoed.filter((t) => t.specificAsset === asset))),
    ]));
    const downtimeWithoutExtras = {};
    Object.entries(assetIncidentMalfunctionData).forEach((singleAsset) => {
        const asset = singleAsset[0];
        const { incident, malfunction } = assetIncidentMalfunctionData[asset];
        downtimeWithoutExtras[asset] = downTimePerAssetMinimum(asset, incident, malfunction, countMaintenanceTasks(tasks, asset));
    });
    const downtime = {};
    Object.entries(assetData).forEach((singleAsset) => {
        const asset = singleAsset[0];
        const { incident, malfunction } = assetIncidentMalfunctionData[asset];
        const maintenance = countMaintenanceTasks(tasks, asset);
        const shippingTime = shippingtimePerAsset[asset];
        downtime[asset] = downTimePerAsset(asset, incident, malfunction, maintenance, mechanicsKnowledge, shippingTime, singleAsset[1], magazijn, impactBonusPresent, sparePartBonusPresent);
    });
    const availabilityPerAsset = {};
    Object.entries(assetIncidentMalfunctionData).forEach((singleAsset) => {
        const asset = singleAsset[0];
        availabilityPerAsset[asset] = availabilityPercentagePerAsset(downtime[asset]);
    });
    availabilityPerAsset.vormmachineA = availabilityPercentageVm(downtime.vormmachineA, downtime.vormmachineB);
    availabilityPerAsset.vormmachineB = availabilityPerAsset.vormmachineA;
    const availabilityPerAssetArray = [];
    Object.entries(availabilityPerAsset).forEach((asset) => availabilityPerAssetArray.push(asset[1]));
    const factoryAvailability = availabilityFactory(availabilityPerAssetArray);
    // Niet zoals het origineel: daar telde elke vormer apart, met alleen zijn eigen speling,
    // terwijl Documentatie zegt dat ze elkaar opvangen. Nu tellen ze samen (`impactVormers`).
    const vormers = impactVormers(downtime.vormmachineA, downtime.vormmachineB);
    const impactPerAssetArray = [];
    Object.entries(assetData).forEach((singleAsset) => {
        const asset = singleAsset[0];
        if (asset === Asset.vormmachineA || asset === Asset.vormmachineB) {
            impactPerAssetArray.push(vormers);
            return;
        }
        const shippingTime = shippingtimePerAsset[asset];
        impactPerAssetArray.push(impactPerAsset(asset, assetIncidentMalfunctionData[asset].incident, assetIncidentMalfunctionData[asset].malfunction, countMaintenanceTasks(tasks, asset), mechanicsKnowledge, shippingTime, singleAsset[1], magazijn, impactBonusPresent, sparePartBonusPresent));
    });
    return {
        complexityPerAsset,
        yellowSidesPerAsset,
        reliabilityPerAsset,
        downtimeWithoutExtras,
        downtime,
        availabilityPerAsset,
        factoryAvailability,
        impactPerAssetArray,
        shippingtimePerAsset,
    };
};
/**
 * Niet in het origineel als losse functie: de pallets van een week, zoals de spelers ze krijgen
 * (`updateCookies`) en zoals de spelleider ze in de berekening ziet. Het origineel rekende dat
 * op twee plekken; het paneel van de spelleider vergat daar het incident en de modifiers.
 *
 * Een incident op een machine legt de fabriek stil: dan nul. Anders de productie bij de
 * grootste impact, plus de koekjes-modifiers van stakeholders (Eduardo -300, Bjorn +200).
 * Nooit minder dan nul.
 */
export const palletsVanDeWeek = (game, assets, tasks) => {
    const machines = assets ?? game.assets;
    if (Object.values(machines).some((asset) => asset.incident))
        return 0;
    const { impactPerAssetArray } = getCalculations(game, machines, tasks);
    const cookieModifier = game.modifiers
        .filter((m) => m.type === InGameEffect.cookies && m.cookies)
        .map((m) => m.cookies)
        .filter((c) => !!c)
        .reduce((a, b) => a + b, 0);
    return Math.max(0, cookieProduction(impactPerAssetArray) + cookieModifier);
};
